import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { Label } from '@eika/label';
import { RadioButtonPanelOptions, RadioButtonPanels } from '@eika/radio-button';

import { useDebitAccountsQuery } from '@/react-query/hooks/query/accounts/use-debit-accounts';
import { useMultipleCustomersFromAccounts } from '@/react-query/hooks/query/use-customer';
import { useMyDebitCards, useRelatedDebitCards } from '@/react-query/hooks/query/use-debit-cards';

import { useSession } from '@components/app/Session';
import { CouldNotFetchAccountsErrorCard } from '@components/error-cards/ErrorCard';
import Footer from '@components/footer/Footer';
import SkeletonList from '@components/skeleton/SkeletonList';
import { CardType } from '@generated-types';
import { useOrderAvailableAccounts } from '@hooks/account/use-order-available-accounts';
import useText from '@hooks/use-text';
import { formatAccountNumber } from '@utils/account-utils';

import './OrderCardFormSelectAccountContainer.scss';

type SelectAccountProps = {
    toNextStep(): void;
};

export const OrderCardFormSelectAccountContainer = ({ toNextStep }: SelectAccountProps) => {
    const { transferFormSharedBy, formPleaseFillOutAlternative, orderCardTitle } = useText();
    const session = useSession();
    const [selectAccountError, setSelectAccountError] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams({ kontonummer: '' });
    const accountNumberFromParam = searchParams.get('kontonummer');

    const { isFetched: debitAccountsFetched, isError: failedFetchingAvailableBankAccounts } = useDebitAccountsQuery();
    const { isFetched: myDebitCardsFetched } = useMyDebitCards();

    const { isFetched: relatedDebitCardsFetched } = useRelatedDebitCards();

    const accounts = useOrderAvailableAccounts();
    const availableAccounts = useMemo(() => [...new Set(accounts)], [accounts]);

    const uniqueAccounts = useMemo(() => {
        const uniqueAccounts = [
            ...new Map(
                availableAccounts
                    .filter((account) => account.owner !== session.user?.userId)
                    .map((account) => [account.owner, account]),
            ).values(),
        ];
        return uniqueAccounts;
    }, [availableAccounts, session.user?.userId]);

    const { data: allCustomerAccounts, isLoading: allCustomersLoading } = useMultipleCustomersFromAccounts(
        CardType.DEBIT,
        uniqueAccounts,
    );

    const next = () => {
        if (!accountNumberFromParam) {
            setSelectAccountError(formPleaseFillOutAlternative);
            return;
        }

        toNextStep();
    };

    const handleChange = (kontonummer: string) => {
        setSelectAccountError('');
        const selectedAccount = availableAccounts.find((acc) => acc.number === kontonummer);

        if (selectedAccount) {
            setSearchParams(
                (prev) => {
                    prev.set('kontonummer', kontonummer);
                    prev.set('kontoid', selectedAccount.id);
                    return prev;
                },
                { replace: true },
            );
        }
    };

    const options: RadioButtonPanelOptions[] = [
        ...availableAccounts.map((account) => {
            const customer = allCustomerAccounts?.find((customer) => customer.number === account.owner);
            const isShared = account.owner !== session.user?.userId;
            const labelText = transferFormSharedBy
                .replace('{firstName}', customer?.firstName || '')
                .replace('{lastName}', customer?.lastName || '');

            return {
                value: account.number,
                label: account.agreementName ?? account.productName ?? formatAccountNumber(account.number),
                labels: isShared && customer ? <Label type="warning" size="small" text={labelText} /> : undefined,
                description: formatAccountNumber(account.number),
                key: account.number,
            };
        }),
    ];

    if (
        allCustomersLoading ||
        !debitAccountsFetched ||
        (session?.debitProfile && (!myDebitCardsFetched || !relatedDebitCardsFetched))
    ) {
        return <SkeletonList />;
    }

    if (failedFetchingAvailableBankAccounts) {
        return <CouldNotFetchAccountsErrorCard />;
    }

    return (
        <FormContainer>
            <FormContainer.Content>
                <RadioButtonPanels
                    label={orderCardTitle}
                    options={options}
                    selectedOption={searchParams.get('kontonummer') ?? undefined}
                    onChange={(option) => handleChange(option)}
                    errorMessage={selectAccountError}
                />
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer next={next} />
            </FormContainer.Footer>
        </FormContainer>
    );
};
